import { Button } from "@/components/ui/button";

import { Link } from "react-router-dom";
import { BarChart3, Users, ArrowRight, TrendingUp, Zap, Shield, ChevronRight, BookOpen, Clock, CalendarClock, RefreshCw, ClipboardCheck, Target, MessageSquare } from "lucide-react";
import { getBlogPosts } from "@/data/blogPosts";
import { AnimatedDashboardPreview } from "@/components/AnimatedDashboardPreview";
import { PublicNavbar } from "@/components/PublicNavbar";
import { Footer } from "@/components/Footer";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";

const featureKeys = [
{ icon: Target, titleKey: 'kpiManagement', descKey: 'kpiManagementDesc' },
{ icon: BarChart3, titleKey: 'realtimeDashboards', descKey: 'realtimeDashboardsDesc' },
{ icon: CalendarClock, titleKey: 'sprintPlanning', descKey: 'sprintPlanningDesc' },
{ icon: RefreshCw, titleKey: 'recurringSprintsTitle', descKey: 'recurringSprintsDesc' },
{ icon: ClipboardCheck, titleKey: 'sprintRetroTitle', descKey: 'sprintRetroDesc' },
{ icon: Users, titleKey: 'roleBasedAccess', descKey: 'roleBasedAccessDesc' },
{ icon: MessageSquare, titleKey: 'aiAssistant', descKey: 'aiAssistantDesc' },
{ icon: TrendingUp, titleKey: 'trendAnalysis', descKey: 'trendAnalysisDesc' },
{ icon: Shield, titleKey: 'enterpriseSecurity', descKey: 'enterpriseSecurityDesc' }];


export default function Landing() {
  const { t } = useTranslation();
  const { localePath, currentLang } = useLanguage();

  return <div className="min-h-screen bg-background">
      <PublicNavbar />
      <main>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-6 animate-fade-in">
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-tight">
              {t('landing.heroTitle1')}
              <br />
              <span className="gradient-text">{t('landing.heroTitle2')}</span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('landing.heroDescription')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link to={localePath('/auth?mode=signup')}>
                <Button variant="hero" size="xl" className="gap-2">
                  {t('landing.getStarted')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to={localePath('/dashboard')}>
                <Button variant="hero-outline" size="xl" className="gap-2">
                  {t('landing.viewDemo')}
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>


          {/* Dashboard Preview */}
          <div className="mt-12 relative animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent z-10 pointer-events-none" />
            <AnimatedDashboardPreview />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.featuresSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureKeys.map((feature) => {
              const Icon = feature.icon;
              return <div key={feature.titleKey} className="group p-6 bg-card border border-border rounded-xl hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t(`features.${feature.titleKey}`)}
                </h3>
                <p className="text-muted-foreground">
                  {t(`features.${feature.descKey}`)}
                </p>
              </div>;
            })}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4">
              <BookOpen className="w-4 h-4" />
              {t('landing.fromBlog')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('landing.blogTitle')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.blogSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {getBlogPosts(currentLang).slice(0, 3).map((post) => <Link key={post.id} to={localePath(`/blog/${post.slug}`)} className="group bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2.5 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                      {post.category}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                    {post.content.substring(0, 150)}...
                  </p>
                  <div className="flex items-center gap-2 text-sm text-primary font-medium">
                    {t('landing.readArticle')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>)}
          </div>

          <div className="text-center mt-10">
            <Link to={localePath('/blog')}>
              <Button variant="outline" size="lg" className="gap-2">
                {t('landing.viewAllArticles')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-primary">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-lg text-primary-foreground/80 mb-8">{t('landing.ctaSubtitle')}</p>
          <Link to={localePath('/auth?mode=signup')}>
            <Button size="xl" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2">
              {t('landing.startTrial')}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      </main>
      <Footer />
    </div>;
}